import { useSupabase } from '@packages/supabase'
import {
  Stack,
  Title,
  Group,
  Text,
  Badge,
  ActionIcon,
  Modal,
  TextInput,
  Select,
  Textarea,
  Switch,
  Alert
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Button, SortableTable } from '@packages/ui'
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconRefresh,
  IconLoader
} from '@tabler/icons-react'
import { useState, useEffect } from 'react'
import { useSupaSWR } from '@packages/supabase'
import { useTenants } from '@lib/tenantsContext'

interface DemoScope {
  id: string
  name: string
  industry: string
  language: string
  is_default: boolean
  is_active: boolean
  description: string | null
  created_at: string
}

interface DemoContentRelationship {
  id: string
  scope_id: string | null
  item_type: string
  item_id: string
  master_tenant_id: string
  demo_content_id: string | null
  language: string | null
  display_order: number
  created_at: string
  // Joined data
  item_title?: string
  scope_name?: string
}

const CONTENT_TYPES = [
  { value: 'pages', label: 'Pages' },
  { value: 'user_tasks', label: 'Tasks' },
  { value: 'events', label: 'Events' },
  { value: 'notes', label: 'Notes' },
  { value: 'protocoll_forms', label: 'Protocoll Forms' },
  { value: 'residents', label: 'Residents' },
  { value: 'teams', label: 'Teams' },
  { value: 'contacts', label: 'contacts' },
  { value: 'wallposts', label: 'Wallposts' }
]

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'pl', label: 'Polish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ar', label: 'Arabic' }
]

export const DemoContent = () => {
  const { supabase, user } = useSupabase()
  const { data: tenants = [] } = useTenants()
  const [activeTab, setActiveTab] = useState<string | null>('scopes')
  const [redeployLoading, setRedeployLoading] = useState(false)
  const [redeployStatus, setRedeployStatus] = useState<string | null>(null)
  const [scopeModalOpened, { open: openScopeModal, close: closeScopeModal }] =
    useDisclosure(false)
  const [
    contentModalOpened,
    { open: openContentModal, close: closeContentModal }
  ] = useDisclosure(false)
  const [editingScope, setEditingScope] = useState<DemoScope | null>(null)
  const [editingContent, setEditingContent] =
    useState<DemoContentRelationship | null>(null)

  // Fetch scopes
  const { data: scopes = [], mutate: mutateScopes } = useSupaSWR({
    key: 'demo-scopes',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('demo_scopes')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as DemoScope[]
    }
  })

  // Fetch content relationships
  const { data: contentRelationships = [], mutate: mutateContent } = useSupaSWR(
    {
      key: 'demo-content-relationships',
      queryFn: async () => {
        const { data, error } = await supabase
          .from('demo_content_relationships')
          .select('*, scope:demo_scopes(name)')
          .order('display_order', { ascending: true })

        if (error) throw error

        // Fetch item titles for each relationship
        const relationshipsWithTitles = await Promise.all(
          (data || []).map(async (rel: any) => {
            let itemTitle = 'Unknown'

            try {
              const tableName = rel.item_type

              // Build select columns based on table type
              let selectColumns = 'id'
              if (tableName === 'pages') {
                selectColumns = 'page_title'
              } else if (
                tableName === 'user_tasks' ||
                tableName === 'events' ||
                tableName === 'notes' ||
                tableName === 'wallposts'
              ) {
                selectColumns = 'title'
              } else if (tableName === 'residents') {
                selectColumns = 'preferred_name, first_name, last_name'
              } else if (tableName === 'teams' || tableName === 'contacts') {
                selectColumns = 'name'
              } else {
                // Fallback: try common columns
                selectColumns =
                  'page_title, title, name, preferred_name, first_name, last_name'
              }

              const { data: itemData } = await supabase
                .from(tableName)
                .select(selectColumns)
                .eq('id', rel.item_id)
                .single()

              if (itemData) {
                itemTitle =
                  itemData.page_title ||
                  itemData.title ||
                  itemData.name ||
                  itemData.preferred_name ||
                  `${itemData.first_name || ''} ${itemData.last_name || ''}`.trim() ||
                  'Untitled'
              }
            } catch (e) {
              console.error('Error fetching item title:', e)
            }

            return {
              ...rel,
              item_title: itemTitle,
              scope_name: rel.scope?.name || 'Non-scoped'
            }
          })
        )

        return relationshipsWithTitles as DemoContentRelationship[]
      }
    }
  )

  const handleCreateScope = async (values: any) => {
    const { error } = await supabase.from('demo_scopes').insert({
      name: values.name,
      industry: values.industry,
      language: values.language,
      is_default: values.is_default || false,
      is_active: values.is_active !== false,
      description: values.description || null
    })

    if (error) {
      alert('Error creating scope: ' + error.message)
      return
    }

    mutateScopes()
    closeScopeModal()
  }

  const handleUpdateScope = async (values: any) => {
    if (!editingScope) return

    const { error } = await supabase
      .from('demo_scopes')
      .update({
        name: values.name,
        industry: values.industry,
        language: values.language,
        is_default: values.is_default || false,
        is_active: values.is_active !== false,
        description: values.description || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', editingScope.id)

    if (error) {
      alert('Error updating scope: ' + error.message)
      return
    }

    mutateScopes()
    closeScopeModal()
    setEditingScope(null)
  }

  const handleDeleteScope = async (id: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this scope? This will also delete all associated content relationships.'
      )
    ) {
      return
    }

    const { error } = await supabase.from('demo_scopes').delete().eq('id', id)

    if (error) {
      alert('Error deleting scope: ' + error.message)
      return
    }

    mutateScopes()
    mutateContent()
  }

  const handleCreateContentRelationship = async (values: any) => {
    if (!user?.tenant_id) {
      alert('No tenants ID found')
      return
    }

    const { error } = await supabase.from('demo_content_relationships').insert({
      scope_id: values.scope_id || null,
      item_type: values.item_type,
      item_id: values.item_id,
      master_tenant_id: user.tenant_id,
      demo_content_id: values.demo_content_id || null,
      language: values.language || null,
      display_order: values.display_order || 0
    })

    if (error) {
      alert('Error creating content relationship: ' + error.message)
      return
    }

    mutateContent()
    closeContentModal()
  }

  const handleDeleteContentRelationship = async (id: string) => {
    if (!confirm('Are you sure you want to remove this content from demo?')) {
      return
    }

    const { error } = await supabase
      .from('demo_content_relationships')
      .delete()
      .eq('id', id)

    if (error) {
      alert('Error deleting relationship: ' + error.message)
      return
    }

    mutateContent()
  }

  const handleRedeployDemoContent = async (
    targetTenantId: string,
    scopeId: string | null
  ) => {
    // Get tenants - use admin RPC function to bypass RLS, or fetch from tenants list
    // First try to get from the tenants list (already fetched)
    let tenantData = tenants.find((t: any) => t.id === targetTenantId)

    // If not found in list, fetch fresh data using admin RPC
    if (!tenantData) {
      const { data: tenantsData, error: tenantsError } = await supabase.rpc(
        'admin_fetch_tenants'
      )

      if (tenantsError) {
        throw new Error(`Failed to fetch tenant: ${tenantsError.message}`)
      }

      tenantData = tenantsData?.find((t: any) => t.id === targetTenantId)
    }

    if (!tenantData) {
      throw new Error(`Tenant not found: ${targetTenantId}`)
    }

    // Determine what to deploy:
    // 1. If scope_id provided: deploy that scope
    // 2. If tenants has demo_scope_id: deploy that scope
    // 3. If no scope specified: deploy default scope + non-scoped content
    let scopesToDeploy: (string | null)[] = []

    if (scopeId) {
      // Explicit scope provided - deploy only that
      scopesToDeploy = [scopeId]
    } else if (tenantData.demo_scope_id) {
      // Tenant has a scope - deploy that
      scopesToDeploy = [tenantData.demo_scope_id]
    } else {
      // Try to get default scope
      const { data: defaultScopeId } = await supabase.rpc(
        'get_default_demo_scope',
        {
          p_language: tenantData.language || 'en'
        }
      )

      if (defaultScopeId) {
        scopesToDeploy.push(defaultScopeId)
      }

      // Always include non-scoped content
      scopesToDeploy.push(null)
    }

    // Deploy each scope (including null for non-scoped)
    const results = []
    const errors = []

    for (const deployScopeId of scopesToDeploy) {
      const { data, error } = await supabase.functions.invoke(
        'deploy-demo-content',
        {
          body: {
            operation: 'copy',
            target_tenant_id: targetTenantId,
            scope_id: deployScopeId, // Can be null for non-scoped content
            owner_user_id: tenantData.owner_user_id,
            replace_existing: deployScopeId === scopesToDeploy[0] // Only replace on first call
          }
        }
      )

      if (error) {
        errors.push(
          `Failed to deploy ${deployScopeId ? 'scope' : 'non-scoped content'}: ${error.message}`
        )
      } else {
        results.push(data)
      }
    }

    if (errors.length > 0 && results.length === 0) {
      throw new Error(errors.join('; '))
    }

    // Combine results
    return {
      success: errors.length === 0,
      copied_items: results.flatMap((r: any) => r?.copied_items || []),
      failed_items: results.flatMap((r: any) => r?.failed_items || []),
      errors:
        errors.length > 0
          ? errors
          : results.flatMap((r: any) => r?.errors || [])
    }
  }

  return (
    <Stack gap="xl" c={'white'}>
      <Group justify="space-between">
        <Title order={2}>Demo Content Manager</Title>
      </Group>

      <Group>
        <Button
          active={activeTab === 'scopes'}
          onClick={() => setActiveTab('scopes')}
        >
          Scopes
        </Button>
        <Button
          active={activeTab === 'content'}
          onClick={() => setActiveTab('content')}
        >
          Content Relationships
        </Button>
        <Button
          active={activeTab === 'testing'}
          onClick={() => setActiveTab('testing')}
        >
          Testing
        </Button>
      </Group>

      {activeTab === 'scopes' && (
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="sm">
              Manage demo content scopes (industry + language combinations)
            </Text>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditingScope(null)
                openScopeModal()
              }}
            >
              Create Scope
            </Button>
          </Group>

          <SortableTable
            data={scopes}
            columns={[
              { label: 'Name', key: 'name' },
              { label: 'Industry', key: 'industry' },
              { label: 'Language', key: 'language' },
              { label: 'Default', key: 'is_default' },
              { label: 'Active', key: 'is_active' },
              {
                label: 'Actions',
                render: (_, scope) => (
                  <Group gap="xs">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => {
                        setEditingScope(scope)
                        openScopeModal()
                      }}
                    >
                      <IconEdit size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleDeleteScope(scope.id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                )
              }
            ]}
          />
        </Stack>
      )}

      {activeTab === 'content' && (
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="sm">
              Link content items to scopes for demo content copying
            </Text>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                setEditingContent(null)
                openContentModal()
              }}
            >
              Add Content
            </Button>
          </Group>

          <SortableTable
            columns={[
              { label: 'Type', key: 'item_type' },
              {
                label: 'Content',
                key: 'item_title',
                render: (_, rel) => rel.item_title || rel.item_id
              },
              {
                label: 'Scope',
                key: 'scope_name',
                render: (_, rel) =>
                  rel.scope_name || <Badge color="gray">Non-scoped</Badge>
              },
              {
                label: 'Language',
                key: 'language',
                render: (_, rel) => rel.language || '-'
              },
              {
                label: 'Actions',
                key: 'item_id',
                render: (id) => {
                  return (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleDeleteContentRelationship(id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  )
                }
              }
            ]}
            data={contentRelationships}
          />
        </Stack>
      )}

      {activeTab === 'testing' && (
        <RedeploySection
          tenants={tenants}
          scopes={scopes}
          supabase={supabase}
          loading={redeployLoading}
          status={redeployStatus}
          onRedeploy={(tenantId, scopeId) => {
            setRedeployLoading(true)
            setRedeployStatus(null)
            handleRedeployDemoContent(tenantId, scopeId)
              .then((result) => {
                setRedeployStatus(
                  `Success! Copied ${result.copied_items?.length || 0} items. ${
                    result.failed_items?.length || 0
                  } failed.`
                )
              })
              .catch((error) => {
                setRedeployStatus(`Error: ${error.message}`)
              })
              .finally(() => {
                setRedeployLoading(false)
              })
          }}
        />
      )}

      {/* Scope Modal */}
      <Modal
        opened={scopeModalOpened}
        onClose={() => {
          closeScopeModal()
          setEditingScope(null)
        }}
        title={editingScope ? 'Edit Scope' : 'Create Scope'}
      >
        <ScopeForm
          scope={editingScope}
          onSubmit={editingScope ? handleUpdateScope : handleCreateScope}
          onCancel={() => {
            closeScopeModal()
            setEditingScope(null)
          }}
        />
      </Modal>

      {/* Content Relationship Modal */}
      <Modal
        opened={contentModalOpened}
        onClose={() => {
          closeContentModal()
          setEditingContent(null)
        }}
        title="Add Content to Demo"
        size="lg"
      >
        <ContentRelationshipForm
          scopes={scopes}
          onSubmit={handleCreateContentRelationship}
          onCancel={() => {
            closeContentModal()
            setEditingContent(null)
          }}
        />
      </Modal>
    </Stack>
  )
}

// Scope Form Component
const ScopeForm = ({ scope, onSubmit, onCancel }: any) => {
  const [formData, setFormData] = useState({
    name: scope?.name || '',
    industry: scope?.industry || '',
    language: scope?.language || 'en',
    is_default: scope?.is_default || false,
    is_active: scope?.is_active !== false,
    description: scope?.description || ''
  })

  return (
    <Stack gap="md" c={'black'} className={'text-black'}>
      <TextInput
        label="Name"
        placeholder="e.g., Healthcare"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
      />
      <TextInput
        label="Industry"
        placeholder="e.g., healthcare"
        value={formData.industry}
        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
        required
      />
      <Select
        label="Language"
        data={LANGUAGES}
        value={formData.language}
        onChange={(value) =>
          setFormData({ ...formData, language: value || 'en' })
        }
        required
      />
      <Textarea
        label="Description"
        placeholder="Optional description"
        value={formData.description}
        onChange={(e) =>
          setFormData({ ...formData, description: e.target.value })
        }
      />
      <Switch
        label="Is Default"
        checked={formData.is_default}
        onChange={(e) =>
          setFormData({ ...formData, is_default: e.currentTarget.checked })
        }
        description="Use as default scope for this language"
      />
      <Switch
        label="Is Active"
        checked={formData.is_active}
        onChange={(e) =>
          setFormData({ ...formData, is_active: e.currentTarget.checked })
        }
      />
      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(formData)}>
          {scope ? 'Update' : 'Create'}
        </Button>
      </Group>
    </Stack>
  )
}

// Content Relationship Form Component
const ContentRelationshipForm = ({ scopes, onSubmit, onCancel }: any) => {
  const { supabase, user } = useSupabase()
  const [formData, setFormData] = useState({
    scope_id: '',
    item_type: '',
    item_id: '',
    demo_content_id: '',
    language: '',
    display_order: 0
  })
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searching, setSearching] = useState(false)

  const handleSearchContent = async () => {
    if (!formData.item_type || !user?.tenant_id) return

    setSearching(true)
    try {
      // Build select columns based on a table type
      let selectColumns = 'id'
      if (formData.item_type === 'pages') {
        selectColumns = 'id, page_title'
      } else if (formData.item_type === 'user_tasks') {
        selectColumns = 'id, title'
      } else if (formData.item_type === 'events') {
        selectColumns = 'id, title'
      } else if (formData.item_type === 'notes') {
        selectColumns = 'id, title'
      } else if (formData.item_type === 'residents') {
        selectColumns = 'id, preferred_name, first_name, last_name'
      } else if (formData.item_type === 'teams') {
        selectColumns = 'id, name'
      } else if (formData.item_type === 'contacts') {
        selectColumns = 'id, name'
      } else if (formData.item_type === 'wallposts') {
        selectColumns = 'id, title'
      } else {
        // Fallback: try common columns
        selectColumns =
          'id, page_title, title, name, preferred_name, first_name, last_name'
      }

      const { data, error } = await supabase
        .from(formData.item_type)
        .select(selectColumns)
        .eq('tenant_id', user.tenant_id)
        .limit(50)

      if (error) throw error

      setSearchResults(data || [])
    } catch (error: any) {
      alert('Error searching: ' + error.message)
    } finally {
      setSearching(false)
    }
  }

  useEffect(() => {
    if (formData.item_type) {
      void handleSearchContent()
    }
  }, [formData.item_type])

  const getItemTitle = (item: any) => {
    return (
      item.page_title ||
      item.title ||
      item.name ||
      item.preferred_name ||
      `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
      item.id
    )
  }

  return (
    <Stack gap="md">
      <Select
        label="Content Type"
        data={CONTENT_TYPES}
        value={formData.item_type}
        onChange={(value) =>
          setFormData({ ...formData, item_type: value || '', item_id: '' })
        }
        required
        placeholder="Select content type"
      />

      {formData.item_type && (
        <Select
          label="Content Item"
          data={searchResults.map((item) => ({
            value: item.id,
            label: getItemTitle(item)
          }))}
          value={formData.item_id}
          onChange={(value) =>
            setFormData({ ...formData, item_id: value || '' })
          }
          required
          placeholder={searching ? 'Searching...' : 'Select content item'}
          searchable
        />
      )}

      <Select
        label="Scope (optional - leave empty for non-scoped generic content)"
        data={scopes.map((s: DemoScope) => ({
          value: s.id,
          label: `${s.name} (${s.industry}, ${s.language})`
        }))}
        value={formData.scope_id}
        onChange={(value) =>
          setFormData({ ...formData, scope_id: value || '' })
        }
        clearable
        placeholder="Select scope or leave empty"
      />

      <Select
        label="Language (optional)"
        data={LANGUAGES}
        value={formData.language}
        onChange={(value) =>
          setFormData({ ...formData, language: value || '' })
        }
        clearable
      />

      <TextInput
        label="Demo Content ID (for updates/replacements)"
        placeholder="e.g., welcome-page-v1"
        value={formData.demo_content_id}
        onChange={(e) =>
          setFormData({ ...formData, demo_content_id: e.target.value })
        }
        description="Unique identifier for this demo content. Used to update/replace existing content."
      />

      <TextInput
        label="Display Order"
        type="number"
        value={formData.display_order}
        onChange={(e) =>
          setFormData({
            ...formData,
            display_order: parseInt(e.target.value) || 0
          })
        }
      />

      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() =>
            onSubmit({
              ...formData,
              scope_id: formData.scope_id || null
            })
          }
          disabled={!formData.item_type || !formData.item_id}
        >
          Add Content
        </Button>
      </Group>
    </Stack>
  )
}

// Redeploy Section Component
const RedeploySection = ({
  tenants,
  scopes,
  supabase,
  loading,
  status,
  onRedeploy
}: {
  tenants: any[]
  scopes: DemoScope[]
  supabase: any
  loading: boolean
  status: string | null
  onRedeploy: (tenantId: string, scopeId: string | null) => void
}) => {
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const [selectedScopeId, setSelectedScopeId] = useState<string>('')

  const selectedTenant = tenants.find((t: any) => t.id === selectedTenantId)

  const handleRedeploy = () => {
    if (!selectedTenantId) {
      alert('Please select a tenants')
      return
    }

    if (
      !confirm(
        `Are you sure you want to redeploy demo content to "${selectedTenant?.name}"? This will delete all existing demo content and copy new content.`
      )
    ) {
      return
    }

    onRedeploy(selectedTenantId, selectedScopeId || null)
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm">
          Test demo content deployment by redeploying to an existing tenant
        </Text>
      </Group>

      <Stack gap="md" style={{ maxWidth: 600 }}>
        <Select
          label="Select Tenant"
          placeholder="Choose a tenant to redeploy demo content to"
          data={tenants
            .filter((t: any) => !t.is_master_tenant)
            .map((t: any) => ({
              value: t.id,
              label: `${t.name} (${t.id.slice(0, 8)}...)`
            }))}
          value={selectedTenantId}
          onChange={(value) => {
            setSelectedTenantId(value || '')
            // Auto-select tenants's current scope if available
            if (value) {
              const tenant = tenants.find((t: any) => t.id === value)
              if (tenant?.demo_scope_id) {
                setSelectedScopeId(tenant.demo_scope_id)
              } else {
                setSelectedScopeId('')
              }
            }
          }}
          searchable
        />

        <Select
          label="Scope (optional - uses tenant's current scope if not selected)"
          placeholder="Select scope or leave empty for tenant's current scope"
          data={scopes.map((s) => ({
            value: s.id,
            label: `${s.name} (${s.industry}, ${s.language})`
          }))}
          value={selectedScopeId}
          onChange={(value) => setSelectedScopeId(value || '')}
          clearable
          description={
            selectedTenant?.demo_scope_id
              ? `Tenant's current scope: ${scopes.find((s) => s.id === selectedTenant.demo_scope_id)?.name || 'Unknown'}`
              : 'No scope currently assigned to this tenants'
          }
        />

        {selectedTenant && (
          <Alert color="blue" title="Tenant Info">
            <Text size="sm">
              <strong>Tenant:</strong> {selectedTenant.name}
              <br />
              <strong>Current Scope:</strong>{' '}
              {selectedTenant.demo_scope_id
                ? scopes.find((s) => s.id === selectedTenant.demo_scope_id)
                    ?.name || 'Unknown'
                : 'None'}
              <br />
              <strong>Demo Content Copied:</strong>{' '}
              {selectedTenant.demo_content_copied_at
                ? new Date(
                    selectedTenant.demo_content_copied_at
                  ).toLocaleString()
                : 'Never'}
            </Text>
          </Alert>
        )}

        <Button
          leftSection={
            loading ? (
              <IconLoader size={16} className="animate-spin" />
            ) : (
              <IconRefresh size={16} />
            )
          }
          onClick={handleRedeploy}
          disabled={!selectedTenantId || loading}
          color="blue"
        >
          {loading ? 'Redeploying...' : 'Redeploy Demo Content'}
        </Button>

        {status && (
          <Alert
            color={status.includes('Error') ? 'red' : 'green'}
            title={status.includes('Error') ? 'Error' : 'Success'}
          >
            {status}
          </Alert>
        )}
      </Stack>
    </Stack>
  )
}
